import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Send, Check, Sparkles, FileText, Zap } from 'lucide-react';
import { saveNewsletterEmail } from '../utils/googleSheetsIntegration';

const benefits = [
  { icon: FileText, text: 'Týdenní AI SEO tipy' },
  { icon: Zap, text: 'Checklisty a šablony' },
];

const Newsletter = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    await saveNewsletterEmail(email);
    setIsSubmitting(false);
    setIsSubmitted(true);

    setTimeout(() => {
      setIsSubmitted(false);
      setEmail('');
    }, 3000);
  };

  return (
    <section
      id="newsletter"
      ref={sectionRef}
      className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#0066FF]/8 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div
          className="glass-strong rounded-3xl p-8 md:p-10 relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          {/* Decorative Gradient */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#00D4AA]/15 to-transparent rounded-full blur-3xl" />

          <div className="relative z-10">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#0066FF]/30 mb-4"
                whileHover={{ scale: 1.03 }}
              >
                <Sparkles className="w-4 h-4 text-[#0066FF]" />
                <span className="text-sm text-[#0066FF] font-medium">Newsletter</span>
              </motion.div>

              <h2 className="text-title text-slate-800 mb-3">
                Získejte <span className="text-gradient">praktické tipy</span> pro AI SEO
              </h2>

              <p className="text-body">
                Týdenní tipy, checklisty a novinky ze světa AI vyhledávání.
              </p>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 rounded-full glass-card"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Icon className="w-4 h-4 text-[#00D4AA]" />
                    <span className="text-sm text-slate-600">{benefit.text}</span>
                  </motion.div>
                );
              })}
            </div>

            {/* Form */}
            {isSubmitted ? (
              <motion.div
                className="text-center py-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-16 h-16 rounded-full bg-[#00D4AA]/15 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#00D4AA]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Děkujeme!</h3>
                <p className="text-slate-500">Jste přihlášeni k newsletteru.</p>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <div className="relative flex-grow">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    placeholder="Váš e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/50 border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#00D4AA]/50 focus:ring-2 focus:ring-[#00D4AA]/10 transition-all"
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#0066FF] to-[#00D4AA] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#00D4AA]/25"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Přihlásit
                    </>
                  )}
                </motion.button>
              </form>
            )}

            <p className="text-center text-xs text-slate-400 mt-6">
              Odhlásit se můžete kdykoliv. Žádný spam.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
